import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { CreditCard, CheckCircle2, XCircle } from "lucide-react";

export default function SimulatorPayment() {
    const { paymentId } = useParams();
    const nav = useNavigate();
    const [payment, setPayment] = useState(null);
    const [open, setOpen] = useState(!!paymentId);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!paymentId) return;
        api.get(`/razorpay/payment/${paymentId}`)
            .then((r) => setPayment(r.data))
            .catch(() => setPayment(null));
    }, [paymentId]);

    const fire = async (event) => {
        setBusy(true);
        await api.post(`/razorpay/payment/${paymentId}/event`, { event });
        setResult(event);
        setBusy(false);
    };

    if (!paymentId) {
        return (
            <div className="max-w-2xl" data-testid="simulator-payment-index">
                <h1 className="text-2xl font-bold tracking-tight">Payment simulator</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Payment checkouts are opened automatically from a WhatsApp conversation
                    when the customer selects a paid tier.
                </p>
                <div className="mt-6 p-4 bg-card border border-border rounded-md flex items-center gap-3">
                    <CreditCard className="size-5 text-primary" />
                    <div className="text-sm">
                        Open the{" "}
                        <button
                            onClick={() => nav("/simulator/whatsapp")}
                            className="text-primary underline"
                        >
                            WhatsApp simulator
                        </button>
                        , pick Immediate or Priority, agree, and a link will appear here.
                    </div>
                </div>
            </div>
        );
    }

    if (!payment) {
        return (
            <div className="text-sm text-muted-foreground">
                Payment not found.
            </div>
        );
    }

    return (
        <div
            className="min-h-[calc(100vh-6rem)] flex items-center justify-center"
            data-testid="simulator-payment-checkout"
        >
            <div className="w-full max-w-sm rzp-card rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-sm font-semibold">OMX Digital</div>
                    <div className="text-[11px] text-gray-500 font-mono">
                        MOCK RAZORPAY
                    </div>
                </div>
                <div className="border-t border-gray-200 pt-4">
                    <div className="text-xs text-gray-500">Amount</div>
                    <div className="text-3xl font-bold">{formatMoney(payment.amount)}</div>
                    <div className="text-[11px] text-gray-500 mt-2">
                        Ref: <span className="font-mono">{payment.ticket_ref}</span>
                    </div>
                    <div className="text-[11px] text-gray-500">
                        Phone: <span className="font-mono">{payment.customer_phone}</span>
                    </div>
                </div>
                {result ? (
                    <div
                        className={`mt-5 p-3 rounded-md text-sm flex items-center gap-2 ${
                            result === "captured"
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-red-50 text-red-700"
                        }`}
                    >
                        {result === "captured" ? (
                            <>
                                <CheckCircle2 className="size-4" /> Payment captured. You can close this window.
                            </>
                        ) : (
                            <>
                                <XCircle className="size-4" /> Payment cancelled.
                            </>
                        )}
                    </div>
                ) : (
                    <div className="mt-5 grid grid-cols-2 gap-2">
                        <button
                            data-testid="simulator-pay-success"
                            disabled={busy}
                            onClick={() => fire("captured")}
                            className="py-2 rounded-md bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
                        >
                            Pay successfully
                        </button>
                        <button
                            data-testid="simulator-pay-cancel"
                            disabled={busy}
                            onClick={() => fire("failed")}
                            className="py-2 rounded-md bg-gray-200 text-gray-800 text-sm font-medium disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                )}
                <div className="mt-6 text-[10px] text-gray-400 text-center">
                    This is a sandbox simulator. No actual payment is processed.
                </div>
            </div>
        </div>
    );
}
